import { useCallback, useMemo } from 'react';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { H2, H3, Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';

import {
  OptionSelect,
  type OptionSelectItem,
} from '../../../shared/ui/forms/OptionSelect';
import { OptionMultiSelect } from '../../../shared/ui/forms/OptionMultiSelect';
import { GroupedOptionMultiSelect } from '../../../shared/ui/forms/GroupedOptionMultiSelect';
import {
  tagsToOptionGroups,
  tagMapToSelectedValues,
  selectedValuesToTagMap,
  type TagOptionGroup,
} from '../../../shared/ui/forms/tagOptionCodec';
import { AuditScoreField } from '../../../shared/ui/forms/AuditScoreField';
import { ReviewQuestionToggleField } from './ReviewQuestionToggleField';
import { ConfigField } from './ConfigField';
import { useMessages } from '../../../shared/i18n/useMessages';
import { useCurrentUser } from '../../../shared/auth';
import { useDivisionsQuery } from '../../../shared/data/divisions/useDivisionsQuery';
import { useCategoriesQuery } from '../../../shared/data/categories/useCategoriesQuery';
import { useLocationsQuery } from '../../../shared/data/locations/useLocationsQuery';
import { useTagsQuery } from '../../../shared/data/tags/useTagsQuery';
import {
  AUDIT_SCORE_MAX,
  AUDIT_SCORE_MIN,
  CONFIG_BUNDLE,
  FIELDS_MAX_WIDTH,
  type ConfigKey,
  dtProps,
  nativeDtProps,
  type QuestionConfigCardProps,
  TOGGLE_ROWS,
  TOGGLE_TRACKING_ROOT,
} from './questionConfigShared';

/**
 * Build an option list from fetched `{ value, label }` pairs, guaranteeing
 * every `current` value still appears even if the fetch hasn't returned it
 * (a deactivated / cross-BU selection would otherwise vanish from its own
 * Select and read as "unset"). Fetched options win on label; missing current
 * values are appended labelled by their raw value. De-duped by value.
 */
function unionOptions(
  fetched: readonly OptionSelectItem[],
  current: readonly string[],
): OptionSelectItem[] {
  const byValue = new Map<string, OptionSelectItem>();
  for (const option of fetched) byValue.set(option.value, option);
  for (const value of current) {
    if (!byValue.has(value)) byValue.set(value, { value, label: value });
  }
  return Array.from(byValue.values());
}

/**
 * The "Basic Information" card of the Question Configuration step — overview,
 * business unit (read-only), division, category, audit score, tags, excluded
 * locations, source policy id (read-only), and the four Comment/Attachment
 * requirement toggles.
 *
 * Division, Category, Tags, and excluded Locations are wired to their real
 * option sources (Orbit/Helios) via React Query, scoped to the user's active
 * BU (Tags additionally by program). The single-select and flat multi-selects
 * union their option list with the draft's current selections so a deactivated
 * value never disappears; Tags is a grouped multi-select whose selection
 * round-trips through the draft's `TagMap`. Fully controlled via `patchConfig`.
 */
export function QuestionConfigBasicInformation({
  config,
  patchConfig,
  disabled,
  dataTestId,
}: QuestionConfigCardProps): JSX.Element {
  const { t, f } = useMessages<ConfigKey>(CONFIG_BUNDLE);
  const infoAltText = t('configInfoAltText');

  // Option sources are scoped to the user's active BU (matches the manual
  // question flow, which scopes every option fetch by the logged-in user's
  // business unit — not the draft's policy-inherited `config.businessUnit`).
  // Each hook stays idle until the required scope is present.
  const { businessUnit, programId } = useCurrentUser();
  const businessUnitId = businessUnit ?? '';

  const { data: divisionsData } = useDivisionsQuery({
    businessUnitId,
    programId,
  });
  // Categories are scoped by business unit only — matching the manual question
  // flow, which lists all BU categories (`loadCategories({ businessUnitId,
  // isActive })`) and does NOT filter the list by division. Division is a
  // property on each category, not a list filter, so there is no
  // division→category cascade.
  const { data: categoriesData } = useCategoriesQuery(
    { businessUnitId, isActive: true },
    { enabled: !!businessUnitId },
  );
  const { data: locationsData } = useLocationsQuery({
    businessUnitId,
    isEnabledForBU: true,
  });
  // Tags are BU + program scoped (matching the manual question flow's
  // `loadTags({ businessUnitId, programId, isActive })`); idle until both are
  // present.
  const { data: tagsData } = useTagsQuery(
    { businessUnitId, programId, isActive: true },
    { enabled: !!businessUnitId && !!programId },
  );

  // Fetched option lists, unioned with the draft's current selections so a
  // deactivated / cross-BU value the draft already holds never disappears
  // from its Select. Memoised for the reference-stable-options contract.
  const divisionOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (divisionsData?.divisions ?? []).map((d) => ({
          value: d.divisionName ?? '',
          label: d.divisionName ?? '',
        })),
        config.divisionName ? [config.divisionName] : [],
      ),
    [divisionsData, config.divisionName],
  );
  const categoryOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (categoriesData?.Categories ?? []).map((c) => ({
          value: c.categoryName ?? '',
          label: c.categoryName ?? '',
        })),
        config.categories,
      ),
    [categoriesData, config.categories],
  );
  const locationOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (locationsData?.locations ?? []).map((loc) => ({
          value: loc.locationCode ?? '',
          label: loc.locationCode ?? '',
        })),
        config.locationsExcluded,
      ),
    [locationsData, config.locationsExcluded],
  );

  // Tags — grouped option universe from the tag catalog (active tags/values,
  // alphabetised) and the draft's stored TagMap projected onto the flat list
  // of encoded leaf values the grouped select consumes. Both memoised for the
  // reference-stable-options contract.
  const tagOptionGroups = useMemo<readonly TagOptionGroup[]>(
    () => tagsToOptionGroups(tagsData?.tags ?? []),
    [tagsData],
  );
  const selectedTagValues = useMemo<readonly string[]>(
    () => tagMapToSelectedValues(config.tagsIncluded),
    [config.tagsIncluded],
  );
  const handleDivisionChange = useCallback(
    (value: string) => patchConfig({ divisionName: value }),
    [patchConfig],
  );
  const handleTagsChange = useCallback(
    (values: string[]) => patchConfig({ tagsIncluded: selectedValuesToTagMap(values) }),
    [patchConfig],
  );

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      {/* Fields are capped to ~two-thirds of the card width (not full-bleed),
       *  matching the manual question workflow which leaves the right third
       *  empty. */}
      <Col gridGap="dimensions.spacing.400" width="100%" maxWidth={FIELDS_MAX_WIDTH}>
        <H2>{t('configStepHeading')}</H2>
        <H3>{t('configBasicHeading')}</H3>

        {/* standards-ignore: DRY:ConfigField — each ConfigField wraps a
         *  DIFFERENT control (Input, single/multi Select, Slider, Chip list)
         *  with its own label, validation, and handler; a config-array map
         *  would have to encode heterogeneous control rendering and would hurt
         *  readability without removing real duplication.
         *  standards-ignore: DRY:Input — the three Inputs (overview, business
         *  unit, source policy) carry unique props/purposes, two of them
         *  read-only; they are not a uniform repeated row.
         *  standards-ignore: DRY:OptionMultiSelect — the Category and Locations
         *  multi-selects have distinct option sources, values, handlers, and
         *  disabled state; not a uniform repeated row. */}
        {/* Overview — required free text. */}
        <ConfigField
          label={t('configOverviewLabel')}
          info={t('configOverviewInfo')}
          infoAltText={infoAltText}
          isRequired
        >
          <Input
            value={config.overview}
            onChange={(e) => patchConfig({ overview: e.target.value })}
            placeholder={t('configOverviewPlaceholder')}
            disabled={disabled}
            aria-required
            // ConfigField's label is a visual Text (not a <label>) and the
            // labelId is only wired on the renderLabel path, so give the plain
            // Input its own accessible name from the same label copy.
            aria-label={t('configOverviewLabel')}
            {...nativeDtProps(dataTestId, 'overview')}
          />
        </ConfigField>

        {/* Business Unit — inherited from the source policy, read-only. */}
        <ConfigField
          label={t('configBusinessUnitLabel')}
          info={t('configBusinessUnitInfo')}
          infoAltText={infoAltText}
        >
          <Input
            value={config.businessUnit}
            readOnly
            disabled={disabled}
            aria-label={t('configBusinessUnitLabel')}
            {...nativeDtProps(dataTestId, 'business-unit')}
          />
        </ConfigField>

        {/* Division — required single-select (options from useDivisionsQuery). */}
        <ConfigField
          label={t('configDivisionLabel')}
          info={t('configDivisionInfo')}
          infoAltText={infoAltText}
          isRequired
        >
          <OptionSelect
            options={divisionOptions}
            value={config.divisionName || undefined}
            onChange={handleDivisionChange}
            placeholder={t('configDivisionPlaceholder')}
            disabled={disabled}
            isRequired
            ariaLabel={t('configDivisionLabel')}
            {...dtProps(dataTestId, 'division')}
          />
        </ConfigField>

        {/* Category — optional multi-select (options from useCategoriesQuery,
         *  scoped by business unit). */}
        <ConfigField
          label={t('configCategoryLabel')}
          info={t('configCategoryInfo')}
          infoAltText={infoAltText}
        >
          <OptionMultiSelect
            options={categoryOptions}
            values={config.categories}
            onChange={(values) => patchConfig({ categories: values })}
            placeholder={t('configCategoryPlaceholder')}
            disabled={disabled}
            ariaLabel={t('configCategoryLabel')}
            {...dtProps(dataTestId, 'category')}
          />
        </ConfigField>

        {/* Audit Score — required 1–20 slider. */}
        <ConfigField
          label={t('configAuditScoreLabel')}
          info={t('configAuditScoreInfo')}
          infoAltText={infoAltText}
          isRequired
          renderLabel={(labelId) => (
            <AuditScoreField
              value={config.auditScore}
              onChange={(value) => patchConfig({ auditScore: value })}
              min={AUDIT_SCORE_MIN}
              max={AUDIT_SCORE_MAX}
              labelId={labelId}
              formatValueText={(score) =>
                f('configAuditScoreValueText', { score, max: AUDIT_SCORE_MAX })
              }
              selectAriaLabel={t('configAuditScoreSelectAriaLabel')}
              disabled={disabled}
              {...dtProps(dataTestId, 'audit-score')}
            />
          )}
        />

        {/* Tags Included — required grouped multi-select (tag group → values,
         *  options from useTagsQuery). The grouped shape matches the manual
         *  question flow; selection round-trips through the draft's TagMap. */}
        <ConfigField
          label={t('configTagsIncludedLabel')}
          info={t('configTagsIncludedInfo')}
          infoAltText={infoAltText}
          isRequired
        >
          <GroupedOptionMultiSelect
            optionGroups={tagOptionGroups}
            values={selectedTagValues}
            onChange={handleTagsChange}
            placeholder={t('configTagsIncludedPlaceholder')}
            disabled={disabled}
            isRequired
            ariaLabel={t('configTagsIncludedLabel')}
            chipDismissAltText={t('configTagsIncludedRemoveAltText')}
            {...dtProps(dataTestId, 'tags')}
          />
        </ConfigField>

        {/* Locations Excluded — a multi-select (options from useLocationsQuery)
         *  that owns both add and remove; its built-in tags show the current
         *  exclusions, so no separate chip list. */}
        <ConfigField
          label={t('configLocationsExcludedLabel')}
          info={t('configLocationsExcludedInfo')}
          infoAltText={infoAltText}
        >
          <OptionMultiSelect
            options={locationOptions}
            values={config.locationsExcluded}
            onChange={(values) => patchConfig({ locationsExcluded: values })}
            placeholder={t('configLocationsExcludedPlaceholder')}
            disabled={disabled}
            ariaLabel={t('configLocationsExcludedLabel')}
            {...dtProps(dataTestId, 'locations')}
          />
        </ConfigField>

        {/* Source Policy ID — auto-populated (WHIZ-002 AC7), read-only. */}
        <ConfigField
          label={t('configSourcePolicyLabel')}
          info={t('configSourcePolicyInfo')}
          infoAltText={infoAltText}
        >
          {config.sourcePolicyId ? (
            <Input
              value={config.sourcePolicyId}
              readOnly
              disabled={disabled}
              aria-label={t('configSourcePolicyLabel')}
              {...nativeDtProps(dataTestId, 'source-policy')}
            />
          ) : (
            <Text
              color="color.text.secondary"
              {...nativeDtProps(dataTestId, 'source-policy-empty')}
            >
              {t('configSourcePolicyEmpty')}
            </Text>
          )}
        </ConfigField>

        {/* Requirement toggles — 2×2 grid (Comment | Attachment columns; Fail
         *  row then Pass row), matching the manual question flow. Each toggle
         *  gets a ConfigField label-on-top with an info tooltip. */}
        {TOGGLE_ROWS.map((row) => (
          <Row
            key={row.map((toggle) => toggle.field).join('-')}
            gridGap="dimensions.spacing.400"
            flexWrap="wrap"
          >
            {row.map((toggle) => (
              <View key={toggle.field} flex={1} minWidth={0}>
                <ConfigField
                  label={t(toggle.labelKey)}
                  info={t(toggle.infoKey)}
                  infoAltText={infoAltText}
                  renderLabel={(labelId) => (
                    <ReviewQuestionToggleField
                      checked={config[toggle.field]}
                      onChange={(checked) => patchConfig({ [toggle.field]: checked })}
                      trackingId={`${TOGGLE_TRACKING_ROOT}.${toggle.trackingSuffix}`}
                      labelId={labelId}
                      disabled={disabled}
                      {...dtProps(dataTestId, toggle.testSuffix)}
                    />
                  )}
                />
              </View>
            ))}
          </Row>
        ))}
      </Col>
    </Card>
  );
}
